import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule, MatCheckboxChange } from '@angular/material/checkbox';
import { AlertCardComponent } from '../alert-card/alert-card.component';
import { TipoImpostoImpressao } from '../../services/models/vendas/config-orcamento.model';

interface ImpostoGrupo {
  titulo: string;
  itens: TipoImpostoImpressao[];
}

/**
 * Painel "Impostos impressos nos itens" das Configurações dos Orçamentos, reutilizável pela
 * Configuração do Pedido de Venda. Lista os impostos (lookup vindo do host via `impostos`) agrupados
 * por `grupo`/`ordemGrupo`/`ordem`, com seleção via `value` (ids). Não faz chamada HTTP e não tem
 * botão de salvar: quem grava o valor é o Salvar da tela hospedeira, que escuta `valueChange`.
 */
@Component({
  selector: 'app-impostos-impressos-selecao',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    AlertCardComponent,
  ],
  templateUrl: './impostos-impressos-selecao.component.html',
  styleUrls: ['./impostos-impressos-selecao.component.css'],
})
export class ImpostosImpressosSelecaoComponent implements OnChanges {
  @Input() impostos: TipoImpostoImpressao[] = [];
  @Input() value: number[] = [];
  @Input() disabled: boolean = false;
  @Output() valueChange = new EventEmitter<number[]>();

  public painelAberto = false;
  public grupos: ImpostoGrupo[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['impostos']) {
      this.grupos = this.agruparImpostos(this.impostos || []);
    }
  }

  public get totalSelecionados(): number {
    return (this.value || []).length;
  }

  public get totalImpostos(): number {
    return (this.impostos || []).length;
  }

  public get semSelecao(): boolean {
    return this.totalSelecionados === 0;
  }

  public get resumoLabel(): string {
    if (this.semSelecao) return 'Nenhum imposto selecionado';
    return `${this.totalSelecionados} de ${this.totalImpostos} impostos selecionados`;
  }

  public togglePainel(): void {
    this.painelAberto = !this.painelAberto;
  }

  public isSelecionado(id: number): boolean {
    return (this.value || []).includes(id);
  }

  public onToggleImposto(id: number, change: MatCheckboxChange): void {
    if (this.disabled) return;

    // Remove antes de acrescentar: garante um id por seleção mesmo que o host devolva um `value` que
    // já contenha o id. Sem isso o array cresceria com repetidos e o resumo passaria a contar mais
    // selecionados que o total de impostos do domínio.
    const semId = (this.value || []).filter(x => x !== id);
    this.valueChange.emit(change.checked ? [...semId, id] : semId);
  }

  public marcarTodos(): void {
    if (this.disabled) return;
    this.valueChange.emit((this.impostos || []).map(i => i.idTipoImpostoImpressao));
  }

  public limpar(): void {
    if (this.disabled) return;
    this.valueChange.emit([]);
  }

  private agruparImpostos(impostos: TipoImpostoImpressao[]): ImpostoGrupo[] {
    const porGrupo = new Map<string, { ordemGrupo: number; itens: TipoImpostoImpressao[] }>();

    for (const imposto of impostos) {
      const grupo = porGrupo.get(imposto.grupo);
      if (grupo) {
        grupo.itens.push(imposto);
      } else {
        porGrupo.set(imposto.grupo, { ordemGrupo: imposto.ordemGrupo, itens: [imposto] });
      }
    }

    return Array.from(porGrupo.entries())
      .sort((a, b) => a[1].ordemGrupo - b[1].ordemGrupo)
      .map(([titulo, { itens }]) => ({
        titulo,
        itens: [...itens].sort((a, b) => a.ordem - b.ordem),
      }));
  }
}
